// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Group, Input, Select, Stack, TextInput } from '@mantine/core';
import { isEmail, useForm } from '@mantine/form';
import type { Questionnaire, QuestionnaireItem, QuestionnaireResponse } from '@medplum/fhirtypes';
import {
  buildQuestionnaireResponse,
  isNpi10,
  isNpiLuhnValid,
  isUsPhone10,
  normalizeUsPhoneToE164,
} from '@polarissleep/shared';
import type { JSX } from 'react';
import { IMaskInput } from 'react-imask';

interface ProviderInfoFormProps {
  questionnaire: Questionnaire;
  onSubmit: (response: QuestionnaireResponse) => void;
  onBack?: () => void;
  submitting?: boolean;
}

interface ProviderInfoFormValues {
  'provider-title': string;
  'provider-first-name': string;
  'provider-last-name': string;
  'practice-name': string;
  'provider-secure-email': string;
  npi: string;
  'provider-phone': string;
  'provider-fax': string;
}

function getStringOptions(item?: QuestionnaireItem): string[] {
  return (item?.answerOption ?? []).map((opt) => opt.valueString).filter((s): s is string => typeof s === 'string');
}

export function ProviderInfoForm({ questionnaire, onSubmit, onBack, submitting }: ProviderInfoFormProps): JSX.Element {
  const titleOptions = getStringOptions(questionnaire.item?.find((i) => i.linkId === 'provider-title'));

  const form = useForm<ProviderInfoFormValues>({
    initialValues: {
      'provider-title': '',
      'provider-first-name': '',
      'provider-last-name': '',
      'practice-name': '',
      'provider-secure-email': '',
      npi: '',
      'provider-phone': '',
      'provider-fax': '',
    },
    validate: {
      'provider-title': (v) => (v ? null : 'Title is required'),
      'provider-first-name': (v) => (v.trim() ? null : 'First name is required'),
      'provider-last-name': (v) => (v.trim() ? null : 'Last name is required'),
      'practice-name': (v) => (v.trim() ? null : 'Practice name is required'),
      'provider-secure-email': isEmail('Enter a valid secure email address'),
      npi: (v) => {
        if (!isNpi10(v)) {
          return 'NPI must be exactly 10 digits';
        }
        if (!isNpiLuhnValid(v)) {
          return 'NPI checksum is invalid';
        }
        return null;
      },
      'provider-phone': (v) => (isUsPhone10(v) ? null : 'Enter a valid 10-digit US phone number'),
      'provider-fax': (v) => (isUsPhone10(v) ? null : 'Enter a valid 10-digit US fax number'),
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    const qrValues: Record<string, unknown> = {
      'provider-title': values['provider-title'],
      'provider-first-name': values['provider-first-name'].trim(),
      'provider-last-name': values['provider-last-name'].trim(),
      'practice-name': values['practice-name'].trim(),
      'provider-secure-email': values['provider-secure-email'].trim(),
      npi: values.npi.trim(),
      'provider-phone': normalizeUsPhoneToE164(values['provider-phone']) ?? values['provider-phone'],
      'provider-fax': normalizeUsPhoneToE164(values['provider-fax']) ?? values['provider-fax'],
    };
    onSubmit(buildQuestionnaireResponse(questionnaire, qrValues));
  });

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Stack gap="sm">
        <Group grow>
          <Select
            label="Title"
            data={titleOptions}
            withAsterisk
            searchable={false}
            {...form.getInputProps('provider-title')}
          />
          <TextInput label="Practice Name" withAsterisk {...form.getInputProps('practice-name')} />
        </Group>
        <Group grow>
          <TextInput label="First Name" withAsterisk {...form.getInputProps('provider-first-name')} />
          <TextInput label="Last Name" withAsterisk {...form.getInputProps('provider-last-name')} />
        </Group>
        <TextInput
          label="Secure Email"
          inputMode="email"
          autoComplete="email"
          placeholder="provider@practice.example"
          withAsterisk
          {...form.getInputProps('provider-secure-email')}
        />
        <Group grow>
          <TextInput
            label="NPI"
            placeholder="10-digit NPI number"
            withAsterisk
            maxLength={10}
            {...form.getInputProps('npi')}
          />
          <Input.Wrapper label="Phone number" withAsterisk {...form.getInputProps('provider-phone')}>
            <Input
              component={IMaskInput}
              mask="(000) 000-0000"
              placeholder="(555) 555-5555"
              {...form.getInputProps('provider-phone')}
            />
          </Input.Wrapper>
        </Group>
        <Input.Wrapper label="Fax" withAsterisk {...form.getInputProps('provider-fax')}>
          <Input
            component={IMaskInput}
            mask="(000) 000-0000"
            placeholder="(555) 555-5555"
            {...form.getInputProps('provider-fax')}
          />
        </Input.Wrapper>
        <Group justify="flex-end" mt="md">
          {onBack && (
            <Button variant="subtle" color="blue" onClick={onBack} disabled={submitting} type="button">
              Back
            </Button>
          )}
          <Button type="submit" loading={submitting} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Continue'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
